(() => {
  if (customElements.get('valeria-buy-now')) return;

  // Shared across Prestige's replacement buy-buttons nodes during variant rendering.
  let active = null;
  let outcomeUncertain = false;
  const controls = () => document.querySelectorAll('valeria-buy-now');
  const refresh = () => controls().forEach(control => control.refresh());
  const bundleSelector = '[data-bundle-pdp-block], [data-mix-match-root], .mix-match';
  const selectionMessage = 'Please finish selecting an available option, then try again.';
  const uncertainMessage = 'We could not confirm your cart. Please review your cart before trying again. Buy it now is paused until you reload this page.';

  async function request(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, {
        credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal,
      });
      const body = await response.json();
      return { response, body };
    } finally {
      clearTimeout(timeout);
    }
  }

  function signature(data) {
    return JSON.stringify(Array.from(data, ([key, value]) => [key,
      typeof value === 'string' ? value : [value.name, value.size, value.lastModified],
    ]));
  }

  class ValeriaBuyNow extends HTMLElement {
    connectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = new AbortController();
      this.button = this.querySelector('button');
      this.status = this.querySelector('[role="status"]');
      this.form = document.getElementById(this.dataset.formId);
      this.section = this.closest('[id^="shopify-section-"]');
      this.root = this.dataset.root;
      this.selecting = false;
      if (!this.button || !this.status || !this.form || !this.section ||
          !/^\/[^?#]*\/$|^\/$/.test(this.root) || this.root.startsWith('//') ||
          !/^\d+$/.test(this.dataset.productId)) return;

      this.button.addEventListener('click', () => this.buy(), { signal: this.lifecycle.signal });
      // Prestige updates the hidden variant only AFTER its section request resolves.
      document.addEventListener('change', event => {
        const picker = event.target.closest?.('variant-picker');
        const externalOptionForm = event.target.matches?.('input[data-option-position]') ?
          event.target.getAttribute('form') : null;
        if (picker?.getAttribute('form-id') === this.dataset.formId || externalOptionForm === this.dataset.formId) {
          this.selecting = true;
          this.refresh();
        }
      }, { capture: true, signal: this.lifecycle.signal });
      this.refresh();
      this.check();
    }

    disconnectedCallback() { this.lifecycle?.abort(); }

    bundleMarkup() { return Boolean(this.section?.querySelector(bundleSelector)); }

    validVariant() {
      return !this.selecting && this.dataset.available === 'true' &&
        /^\d+$/.test(this.dataset.variantId) &&
        String(this.form?.elements.namedItem('id')?.value) === this.dataset.variantId;
    }

    message(text) { this.status.textContent = text; }

    refresh() {
      if (!this.button) return;
      this.button.disabled = !this.validVariant() ||
        this.bundleMarkup() || Boolean(active) || outcomeUncertain;
      this.button.setAttribute('aria-busy', String(Boolean(active)));
      this.button.textContent = active ? 'PLEASE WAIT…' : 'BUY IT NOW';
      if (outcomeUncertain) this.message(uncertainMessage);
    }

    check() {
      if (this.bundleMarkup()) {
        this.hidden = true;
        return;
      }
      this.message(this.validVariant() ? '' : selectionMessage);
      this.refresh();
    }

    async buy() {
      if (active || outcomeUncertain || !this.validVariant() || this.bundleMarkup()) return;
      // Native Prestige ADD TO CART and the sticky submit button already set aria-busy.
      if (Array.from(this.form.elements).some(input => input.type === 'submit' && input.getAttribute('aria-busy') === 'true')) return;
      if (!this.form.reportValidity()) return;

      const data = new FormData(this.form);
      const quantity = data.get('quantity') ?? '1';
      if (data.getAll('id').length !== 1 || data.getAll('quantity').length > 1 ||
          !/^\d+$/.test(quantity) || !Number.isSafeInteger(Number(quantity)) || Number(quantity) < 1 ||
          Array.from(data.keys()).some(key => /^properties\[_bundle|^items\[|^parent_/.test(key))) {
        this.message('This selection cannot use Buy it now. Please use Add to Cart.');
        return;
      }
      const before = signature(data);
      active = { formId: this.dataset.formId };
      refresh();
      let sent = false;
      let redirecting = false;
      try {
        if (!this.isConnected || !this.validVariant() || this.bundleMarkup() ||
            signature(new FormData(this.form)) !== before || !this.form.reportValidity()) {
          throw new Error(selectionMessage);
        }
        data.set('quantity', quantity);
        data.delete('sections');
        data.delete('sections_url');
        data.delete('on_success');
        sent = true;
        const { response, body } = await request(`${this.root}cart/add.js`, {
          method: 'POST', body: data,
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok || response.redirected) {
          throw new Error(typeof body?.description === 'string' ? body.description : 'The selected item could not be added.');
        }
        const items = Array.isArray(body?.items) ? body.items : [body];
        if (items.length !== 1 || String(items[0]?.variant_id ?? items[0]?.id) !== data.get('id') ||
            !Number.isSafeInteger(items[0]?.quantity) || items[0].quantity < Number(quantity)) {
          throw new Error('The cart response could not be confirmed.');
        }
        // No variant:add/cart:change events: those intentionally open Prestige's drawer.
        // The existing cart is retained, including matching lines whose quantities increase.
        location.assign(`${this.root}checkout`);
        redirecting = true;
      } catch (error) {
        // Shopify can partially add stock even on 422; never retry a possibly accepted POST.
        if (sent) outcomeUncertain = true;
        const message = sent ? `${error.message || 'Unable to confirm the request.'} ${uncertainMessage}` :
          (error.message === selectionMessage ? selectionMessage : 'Buy it now is unavailable for this selection. Please use Add to Cart.');
        controls().forEach(control => control.message(message));
      } finally {
        if (!redirecting) {
          active = null;
          refresh();
        }
      }
    }
  }

  // Prevent a second native/sticky form submit only while this separate flow is in flight.
  document.addEventListener('submit', event => {
    if (active && event.target.getAttribute('id') === active.formId) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
  // Returning from checkout via the browser's back cache must not submit a second add.
  window.addEventListener('pageshow', event => {
    if (event.persisted && active) {
      active = null;
      outcomeUncertain = true;
      refresh();
    }
  });
  customElements.define('valeria-buy-now', ValeriaBuyNow);
})();
